import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { message, Row, Spin, Empty, Typography } from "antd";
// useSelector is no longer needed as we get filters from props
import { 
  FilterControls,
  createGeoJsonData,
  SummaryStatisticsCards,
  CustomerReportTabs
} from "../../components/CustomerReports";
import { getCustomerLocationMap } from '../../core/api/dashboard';
// CustomerFilters is now handled by parent component
import './components/CustomerReportStyles.css';

const { Title } = Typography;

const CustomerReports = ({ saleTypeFilters, userSelectedFilters }) => {
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [searchCity, setSearchCity] = useState('');
  
  // Filter states are now passed as props from parent component
  
  // Use a ref to track if this is the initial render
  const initialRenderRef = useRef(true);
  const previousFiltersRef = useRef(null);
  // Define the center point of North America (USA and Canada)
  const northAmericaCenter = {
    longitude: -95.7129,  // Centered between East and West coasts
    latitude: 45.0,       // Centered between Southern US and Northern Canada
  };
  
  const [viewState, setViewState] = useState({
    ...northAmericaCenter,  // Always start centered on North America
    zoom: 3.2,              // Slightly zoomed out to show both countries
    minZoom: 2.5,           // Prevent zooming out too far to maintain focus
    maxZoom: 10,            // Prevent zooming in too much
    bearing: 0,             // Keep north orientation
    pitch: 0,               // Keep flat view
    padding: { top: 20, bottom: 20, left: 20, right: 20 } // Add padding to ensure visibility
  });
  const [popupInfo, setPopupInfo] = useState(null);
  const [geoJsonData, setGeoJsonData] = useState(null);
  const [activeTab, setActiveTab] = useState('map');

  // Filters are now fetched in the parent component

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Handle sale types the same way as SalesDashboard
      let saleTypes = [];
      
      if (userSelectedFilters.saleTypes && userSelectedFilters.saleTypes.length > 0) {
        saleTypes = userSelectedFilters.saleTypes;
      } else {
        console.log('Using sale types from saleTypeFilters object');
        // Convert the saleTypeFilters object to an array of selected types
        saleTypes = Object.keys(saleTypeFilters).filter(key => saleTypeFilters[key]);
      }
      
      // If no sale types are selected, select all by default
      if (saleTypes.length === 0) {
        console.log('No sale types selected, defaulting to all');
        saleTypes = ['phone', 'mail', 'web', 'unknown'];
      }
      
      console.log('Final sale types being sent to backend:', saleTypes);
      
      const filterPayload = {
        dateRange: userSelectedFilters.dateRange,
        brands: userSelectedFilters.brands || [],
        saleTypes: saleTypes,
        Product: userSelectedFilters.Product || [],
        Category: userSelectedFilters.Category || [],
        Segment: userSelectedFilters.Segment || [],
        lists: userSelectedFilters.lists || [],
        selectedBrand: selectedBrand || null,
        searchCity: searchCity || ''
      };
      
      console.log('Sending filters to API:', filterPayload);
      const data = await getCustomerLocationMap(filterPayload);
      console.log("Customer location data:", data);
      
      // Validate the API response structure
      if (!data) {
        throw new Error('No data received from API');
      }
      
      if (!data.locations || !Array.isArray(data.locations)) {
        console.error('API response missing locations array:', data);
        throw new Error('Invalid API response format: missing locations array');
      }
      
      console.log(`API returned ${data.locations.length} location records`);
      
      // Sample the first few records to verify structure
      if (data.locations.length > 0) {
        console.log('Sample location data:', data.locations[0]);
      }
      
      setMapData(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching customer data:", error);
      message.error('Failed to load customer location data: ' + (error.message || 'Unknown error'));
      // Initialize with empty data to prevent errors
      setMapData({ locations: [], state_summary: {}, top_cities: { by_customers: [] } });
      setLoading(false);
    }
  }, [selectedBrand, searchCity, userSelectedFilters, saleTypeFilters]);

  // Client selection is now handled in the parent component
  
  useEffect(() => {
    // Skip the initial render to prevent double data fetch
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      previousFiltersRef.current = JSON.stringify({
        userSelectedFilters,
        saleTypeFilters,
        selectedBrand,
        searchCity
      });
      fetchData();
      return;
    }
    
    // Check if filters have actually changed
    const currentFilters = JSON.stringify({
      userSelectedFilters,
      saleTypeFilters,
      selectedBrand,
      searchCity
    });
    
    if (currentFilters !== previousFiltersRef.current) {
      previousFiltersRef.current = currentFilters;
      fetchData();
    } else {
      console.log('Filters unchanged, skipping data fetch');
    }
  }, [userSelectedFilters, saleTypeFilters, selectedBrand, searchCity, fetchData]);

  // Memoize filtered locations to avoid unnecessary recalculations
  const filteredLocations = useMemo(() => {
    if (!mapData?.locations) {
      console.log('No locations data available in mapData');
      return [];
    }
    
    console.log(`Total locations from API: ${mapData.locations.length}`);
    
    // Filter locations by city name if search is provided
    const filtered = mapData.locations.filter(loc => {
      // Skip invalid locations
      if (!loc.city || !loc.state) return false;
      
      // Apply city filter if provided
      if (!searchCity || searchCity.trim() === '') return true;
      return loc.city.toLowerCase().includes(searchCity.trim().toLowerCase());
    });
    
    console.log(`Filtered locations: ${filtered.length}`);
    return filtered;
  }, [mapData, searchCity]);
  
  // Calculate summary statistics for the filtered data
  const filteredStats = useMemo(() => {
    if (filteredLocations.length === 0) return {
      totalCustomers: 0,
      totalSales: 0,
      totalOrders: 0,
      ordersPerCustomer: 0
    };
    
    const stats = filteredLocations.reduce((acc, loc) => {
      acc.totalCustomers += parseInt(loc.customer_count) || 0;
      acc.totalSales += parseFloat(loc.total_sales) || 0;
      acc.totalOrders += parseInt(loc.order_count) || 0;
      return acc;
    }, { totalCustomers: 0, totalSales: 0, totalOrders: 0 });
    
    stats.ordersPerCustomer = stats.totalCustomers > 0 ? 
      stats.totalOrders / stats.totalCustomers : 0;
    
    return stats;
  }, [filteredLocations]);
  
  // Create GeoJSON data for map when filtered locations change
  useEffect(() => {
    const createGeoData = async () => {
      if (filteredLocations && filteredLocations.length > 0) {
        console.log('Creating GeoJSON data from filteredLocations:', filteredLocations.length);
        try {
          const geoData = await createGeoJsonData(filteredLocations);
          setGeoJsonData(geoData);
          console.log('GeoJSON data created successfully with', geoData.features.length, 'features');
        } catch (error) {
          console.error('Error creating GeoJSON data:', error);
          setGeoJsonData({ type: 'FeatureCollection', features: [] });
        }
      } else {
        console.log('No filtered locations available for GeoJSON creation');
        setGeoJsonData({ type: 'FeatureCollection', features: [] });
      }
    };
    
    createGeoData();
  }, [filteredLocations]);

  // Get top cities for the dropdown
  const topCities = useMemo(() => {
    return mapData?.top_cities?.by_customers || [];
  }, [mapData]);

  // City columns are now in a separate component

  // Summary statistics - combine API data with filtered stats
  const summaryStats = useMemo(() => {
    if (!mapData) return null;
    
    // Use filtered stats when city filter is applied, otherwise use overall stats from API
    const useFilteredStats = searchCity && searchCity.trim() !== '' && filteredStats;
    
    return {
      totalCustomers: useFilteredStats ? filteredStats.totalCustomers : mapData.total_customers,
      totalSales: useFilteredStats ? filteredStats.totalSales : mapData.total_sales,
      totalOrders: useFilteredStats ? filteredStats.totalOrders : mapData.total_orders,
      totalCities: mapData.total_cities,
      totalStates: mapData.total_states,
      ordersPerCustomer: useFilteredStats ? 
        filteredStats.ordersPerCustomer : 
        (mapData.total_customers > 0 ? mapData.total_orders / mapData.total_customers : 0)
    };
  }, [mapData, filteredStats, searchCity]);

  return (
    <div style={{  }}>
      <div className="dashboard-layout">
        <div className="dashboard-main-full px-4 py-3">
      {loading ? (
        <Spin size="large" style={{ margin: '100px auto', display: 'block' }} />
      ) : !mapData?.locations?.length ? (
        <Empty description="No location data" style={{ margin: '100px auto' }} />
      ) : (
        <>
          {/* Summary Statistics Cards */}
          <SummaryStatisticsCards summaryStats={summaryStats} />

          {/* Tabs for different views */}
          <CustomerReportTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            viewState={viewState}
            setViewState={setViewState}
            geoJsonData={geoJsonData}
            popupInfo={popupInfo}
            setPopupInfo={setPopupInfo}
            filteredLocations={filteredLocations}
            mapData={mapData}
          />
        </>
      )}
        </div>
      </div>
    </div>
  );
};

export default CustomerReports;
